import {useEffect, useState} from 'react';
import {useSpring} from '@react-spring/web';
import {getSpringConfig} from '../config/spring-config.config';
import {AnimationSpeed, SpringConfig} from '../types';

type ModalVariant =
    | 'fade-down'
    | 'fade-left'
    | 'pop'
    | 'zoom-out'
    | 'rise'
    | 'flip'
    | 'swing';

interface VariantAnimation {
    from: Record<string, unknown>;
    enter: Record<string, unknown>;
    leave: Record<string, unknown>;
}

export const useModalAnimation = (
    isOpen: boolean,
    speed: AnimationSpeed = 'medium',
    variant: ModalVariant = 'fade-down'
) => {
    const [isClosing, setIsClosing] = useState(false);
    const [onCloseDone, setOnCloseDone] = useState<(() => void) | null>(null);
    const [leaveOverride, setLeaveOverride] = useState<Record<string, unknown> | null>(null);

    const config: SpringConfig = getSpringConfig(speed);
    const animation = MODAL_VARIANTS[variant];

    const [springProps, api] = useSpring(() => ({
        from: animation.from,
        config,
        onRest: () => {
            if (isClosing) {
                setIsClosing(false);
                onCloseDone?.();
                setOnCloseDone(null);
            }
        }
    }), [onCloseDone]);

    const closeModal = (onDone?: () => void, leave?: ModalVariant) => {
        if (leave) setLeaveOverride(MODAL_VARIANTS[leave].leave);
        setIsClosing(true);
        if (onDone) setOnCloseDone(() => onDone);
    };

    useEffect(() => {
        if (isOpen) {
            setIsClosing(false);
            api.set(animation.from);
            api.start({...animation.enter, config});
        }
    }, [isOpen, api, config]);

    useEffect(() => {
        isClosing && api.start(leaveOverride ?? animation.leave);
    }, [isClosing, api]);

    return {springProps, closeModal, isClosing};
};

const MODAL_VARIANTS: Record<ModalVariant, VariantAnimation> = {
    'fade-down': {
        from: {opacity: 0, transform: 'translate3d(0%, 120%, 0)'},
        enter: {opacity: 1, transform: 'translate3d(0%, 0%, 0)'},
        leave: {opacity: 0, transform: 'translate3d(0%, 120%, 0)'},
    },
    'fade-left': {
        from: {opacity: 0, transform: 'translate3d(-100%, 0%, 0)'},
        enter: {opacity: 1, transform: 'translate3d(0%, 0%, 0)'},
        leave: {opacity: 0, transform: 'translate3d(-100%, 0%, 0)'},
    },
    pop: {
        from: {opacity: 0, transform: 'scale(0.7)'},
        enter: {opacity: 1, transform: 'scale(1)'},
        leave: {opacity: 0, transform: 'scale(0.7)'},
    },
    'zoom-out': {
        from: {opacity: 0, transform: 'scale(1.15)'},
        enter: {opacity: 1, transform: 'scale(1)'},
        leave: {opacity: 0, transform: 'scale(1.15)'},
    },
    rise: {
        from: {opacity: 0, transform: 'translate3d(0%, 48px, 0) scale(0.96)'},
        enter: {opacity: 1, transform: 'translate3d(0%, 0%, 0) scale(1)'},
        leave: {opacity: 0, transform: 'translate3d(0%, 48px, 0) scale(0.96)'},
    },
    flip: {
        from: {opacity: 0, transform: 'perspective(1000px) rotateX(-90deg)'},
        enter: {opacity: 1, transform: 'perspective(1000px) rotateX(0deg)'},
        leave: {opacity: 0, transform: 'perspective(1000px) rotateX(90deg)'},
    },
    swing: {
        from: {opacity: 0, transform: 'perspective(1000px) rotateX(-20deg) translate3d(0%, -30px, 0)'},
        enter: {opacity: 1, transform: 'perspective(1000px) rotateX(0deg) translate3d(0%, 0%, 0)'},
        leave: {opacity: 0, transform: 'perspective(1000px) rotateX(-20deg) translate3d(0%, -30px, 0)'},
    },
};
